import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ChevronLeft, Plus, Bug, CreditCard, UserCircle, Sparkles, GraduationCap, AlertTriangle, HelpCircle, ArrowUp } from "lucide-react";

const CATEGORIES = [
  { key: "bug", label: "Bug report", icon: Bug, color: "#A45B4C" },
  { key: "billing", label: "Billing / refund", icon: CreditCard, color: "#7A6A5E" },
  { key: "account", label: "Account help", icon: UserCircle, color: "#3E7A5E" },
  { key: "feature", label: "Feature request", icon: Sparkles, color: "#5E7A73" },
  { key: "coach", label: "Coach / booking", icon: GraduationCap, color: "#8A6B7A" },
  { key: "safety", label: "Safety concern", icon: AlertTriangle, color: "#C57F3F" },
  { key: "other", label: "Something else", icon: HelpCircle, color: "#8A8A8A" },
];

export default function Support() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("support"));
  const [mode, setMode] = useState("list");
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [category, setCategory] = useState("other");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/support/threads"); setThreads(Array.isArray(data) ? data : []); }
    catch { setThreads([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (mode === "list") loadThreads(); }, [mode, loadThreads]);

  const loadMessages = async (id) => {
    try { const { data } = await api.get(`/support/threads/${id}/messages`); setMessages(Array.isArray(data) ? data : []); }
    catch { setMessages([]); }
  };
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const openThread = (id) => { setActiveId(id); setMode("thread"); loadMessages(id); };

  const send = async () => {
    if (!message.trim() || busy) return;
    setBusy(true);
    const body = message.trim();
    if (mode === "thread" && activeId) {
      setMessages((prev) => [...prev, { id: `tmp-${Date.now()}`, role: "user", text: body }]);
    }
    try {
      await api.post("/support/message", {
        category: mode === "thread" ? (threads.find((x) => x.id === activeId)?.category || "other") : category,
        message: body,
        thread_id: mode === "thread" ? activeId : undefined,
      });
      setMessage("");
      if (mode === "composer") {
        const { data } = await api.get("/support/threads");
        setThreads(Array.isArray(data) ? data : []);
        if (data?.[0]) openThread(data[0].id);
      } else if (activeId) await loadMessages(activeId);
    } catch (e) { toast.error(apiError(e)); setMessage(body); }
    finally { setBusy(false); }
  };

  const Header = ({ title, onBack, action }) => (
    <div className="flex items-center gap-3">
      <button onClick={onBack} data-testid="support-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
      <h1 className="flex-1 truncate font-display text-xl text-foreground">{title}</h1>
      {action}
    </div>
  );

  if (mode === "composer") {
    return (
      <div className="mx-auto max-w-2xl space-y-4" data-testid="support-composer">
        <Header title={t("new_support_request")} onBack={() => setMode("list")} />
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button key={c.key} onClick={() => setCategory(c.key)} data-testid={`cat-${c.key}`}
              className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors"
              style={category === c.key ? { borderColor: c.color, backgroundColor: c.color + "18", color: c.color } : { borderColor: "hsl(var(--border))", color: "hsl(var(--muted-foreground))" }}>
              <c.icon className="h-3.5 w-3.5" /> {c.label}
            </button>
          ))}
        </div>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("describe_your_issue")} rows={6} data-testid="support-message" className="w-full resize-none rounded-2xl border border-border bg-card p-3.5 text-sm outline-none focus:border-primary" maxLength={4000} />
        <button onClick={send} disabled={!message.trim() || busy} data-testid="support-send" className="w-full rounded-full bg-primary py-3 font-semibold text-primary-foreground disabled:opacity-50">{t("suppor_send")}</button>
      </div>
    );
  }

  if (mode === "thread") {
    const at = threads.find((x) => x.id === activeId);
    return (
      <div className="mx-auto flex h-[calc(100vh-11rem)] max-w-2xl flex-col" data-testid="support-thread">
        <Header title={at?.subject || t("support")} onBack={() => setMode("list")} />
        <div ref={scrollRef} className="mt-3 flex-1 space-y-2 overflow-y-auto rounded-2xl border border-border bg-card p-4">
          {messages.map((m) => (
            <div key={m.id} className={`max-w-[88%] rounded-2xl p-3 text-sm ${m.role === "user" ? "ml-auto bg-primary text-primary-foreground" : m.role === "human" ? "bg-emerald-50 text-foreground" : m.role === "system" ? "mx-auto bg-primary/10 text-foreground" : "bg-secondary/60 text-foreground"}`} data-testid={`support-msg-${m.id}`}>
              {m.role === "ai" ? <p className="mb-1 text-[10px] text-muted-foreground">{t("my_within_ai")}</p> : null}
              {m.role === "human" ? <p className="mb-1 text-[10px] font-semibold text-emerald-600">{t("my_within_team")}</p> : null}
              {m.role === "system" ? <p className="mb-1 text-[10px] text-muted-foreground">{t("status")}</p> : null}
              <p className="leading-relaxed">{m.text}</p>
              {m.escalated && m.role === "ai" ? <p className="mt-1.5 text-[11px] italic text-primary">{t("forwarded_to_a_human_reply_arriving_by")}</p> : null}
            </div>
          ))}
        </div>
        <div className="mt-2 flex items-end gap-2">
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={t("suppor_reply")} rows={1} data-testid="thread-input" className="max-h-28 min-h-[44px] flex-1 resize-none rounded-2xl border border-border bg-card px-3.5 py-2.5 text-sm outline-none focus:border-primary" />
          <button onClick={send} disabled={!message.trim() || busy} data-testid="thread-send" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-50"><ArrowUp className="h-5 w-5" /></button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="support-list">
      <Header title={t("support")} onBack={() => navigate("/app/account")} action={
        <button onClick={() => setMode("composer")} data-testid="support-new" className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground"><Plus className="h-5 w-5" /></button>
      } />
      {loading ? <Spinner label={t("common.loading")} /> : threads.length === 0 ? (
        <button onClick={() => setMode("composer")} data-testid="support-empty-cta" className="flex w-full flex-col items-center gap-2 rounded-2xl border border-dashed border-border p-10 text-center">
          <MessageIcon />
          <p className="font-display text-base text-foreground">{t("start_a_new_support_chat")}</p>
        </button>
      ) : threads.map((th) => {
        const cat = CATEGORIES.find((c) => c.key === th.category) || CATEGORIES[CATEGORIES.length - 1];
        return (
          <button key={th.id} onClick={() => openThread(th.id)} data-testid={`thread-${th.id}`} className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left transition-colors hover:bg-accent">
            <span className="flex h-9 w-9 items-center justify-center rounded-full text-white" style={{ backgroundColor: cat.color }}><cat.icon className="h-4 w-4" /></span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-foreground">{th.subject}</span>
              <span className="block text-xs text-muted-foreground">{cat.label} · {th.status}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function MessageIcon() {
  return <HelpCircle className="h-8 w-8 text-muted-foreground" />;
}
